import queryString from 'query-string';

// External (other job sites) jobs for the /jobs timeline, by posting day.
// Keyed by the board's filter hash plus time zone, since days depend on both.

const LOAD_DAYS = 'twineApp/externalJobs/LOAD_DAYS';
const LOAD_DAYS_SUCCESS = 'twineApp/externalJobs/LOAD_DAYS_SUCCESS';
const LOAD_DAYS_FAIL = 'twineApp/externalJobs/LOAD_DAYS_FAIL';
const LOAD_MORE = 'twineApp/externalJobs/LOAD_MORE';
const LOAD_MORE_SUCCESS = 'twineApp/externalJobs/LOAD_MORE_SUCCESS';
const LOAD_MORE_FAIL = 'twineApp/externalJobs/LOAD_MORE_FAIL';
const RETRY_DAYS = 'twineApp/externalJobs/RETRY_DAYS';

/** External jobs shown per day before "Show all". */
export const EXTERNAL_JOBS_PER_DAY = 10;

/** Jobs fetched per "Show all" request. */
export const EXTERNAL_JOBS_PER_PAGE = 50;

/** Pauses before each browser retry when the API sends no Retry-After. */
const RETRY_DELAYS_MS = [1000, 4000];

const MAX_RETRY_AFTER_MS = 10000;

const initialState = {};

const isServer = () => typeof window === 'undefined';

// Rate limits, server errors and dropped connections can pass; bad requests won't.
const isRetryable = (error) => {
  const status = error && error.status;
  return !status || status === 429 || status >= 500;
};

const retryDelay = (error, attempt) => {
  const header = error && error.response && error.response.header;
  const seconds = Number(header && header['retry-after']);
  return seconds > 0
    ? Math.min(seconds * 1000, MAX_RETRY_AFTER_MS)
    : RETRY_DELAYS_MS[attempt];
};

/**
 * GET that retries in the browser, so a rate-limited request doesn't leave
 * days missing. The server render doesn't wait; the browser loads them.
 *
 * @param {object} client ApiClient
 * @param {string} url
 * @param {number} [attempt]
 * @returns {Promise}
 */
export const getWithRetry = (client, url, attempt = 0) =>
  client.get(url).catch((error) => {
    if (
      isServer() ||
      attempt >= RETRY_DELAYS_MS.length ||
      !isRetryable(error)
    ) {
      throw error;
    }
    return new Promise((resolve) => {
      setTimeout(resolve, retryDelay(error, attempt));
    }).then(() => getWithRetry(client, url, attempt + 1));
  });

const datesBetween = (from, to) => {
  const dates = [];
  const day = new Date(`${to}T00:00:00Z`);
  const end = new Date(`${from}T00:00:00Z`);
  while (day >= end) {
    dates.push(day.toISOString().slice(0, 10));
    day.setUTCDate(day.getUTCDate() - 1);
  }
  return dates;
};

const entryFor = (state, key) =>
  state[key] || { days: {}, fetched: {}, failedDates: {}, loading: false };

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD_DAYS:
      return {
        ...state,
        [action.key]: { ...entryFor(state, action.key), loading: true },
      };

    case LOAD_DAYS_SUCCESS: {
      const entry = entryFor(state, action.key);
      const days = { ...entry.days };
      (action.result.days || []).forEach((day) => {
        days[day.date] = { total: day.total, jobs: day.jobs, loading: false };
      });
      const fetched = { ...entry.fetched };
      const failedDates = { ...entry.failedDates };
      // The API clamps long ranges, so trust its range over the request.
      datesBetween(
        action.result.from || action.from,
        action.result.to || action.to
      ).forEach((date) => {
        fetched[date] = true;
        delete failedDates[date];
      });
      return {
        ...state,
        [action.key]: {
          ...entry,
          days,
          fetched,
          failedDates,
          failed: Object.keys(failedDates).length > 0,
          loading: false,
        },
      };
    }

    case LOAD_DAYS_FAIL: {
      const entry = entryFor(state, action.key);
      // Leave the server render's days unfetched so the browser loads them.
      if (action.onServer) {
        return {
          ...state,
          [action.key]: { ...entry, loading: false },
        };
      }
      // Retries are spent: mark the range fetched so it isn't requested in a
      // loop; those days just show their Twine jobs until the visitor retries.
      const fetched = { ...entry.fetched };
      const failedDates = { ...entry.failedDates };
      datesBetween(action.from, action.to).forEach((date) => {
        fetched[date] = true;
        failedDates[date] = true;
      });
      return {
        ...state,
        [action.key]: {
          ...entry,
          fetched,
          failedDates,
          loading: false,
          failed: true,
        },
      };
    }

    case RETRY_DAYS: {
      const entry = state[action.key];
      if (!entry || !entry.failed) {
        return state;
      }
      // Unmark the failed days so the board requests them again.
      const fetched = { ...entry.fetched };
      Object.keys(entry.failedDates || {}).forEach((date) => {
        delete fetched[date];
      });
      return {
        ...state,
        [action.key]: { ...entry, fetched, failedDates: {}, failed: false },
      };
    }

    case LOAD_MORE:
    case LOAD_MORE_FAIL: {
      const entry = entryFor(state, action.key);
      const day = entry.days[action.date];
      if (!day) {
        return state;
      }
      return {
        ...state,
        [action.key]: {
          ...entry,
          days: {
            ...entry.days,
            [action.date]: { ...day, loading: action.type === LOAD_MORE },
          },
        },
      };
    }

    case LOAD_MORE_SUCCESS: {
      const entry = entryFor(state, action.key);
      const day = entry.days[action.date];
      const loaded = (action.result.days || []).find(
        (d) => d.date === action.date
      );
      if (!day) {
        return state;
      }
      const seen = new Set(day.jobs.map((job) => job.id));
      const more = loaded ? loaded.jobs.filter((job) => !seen.has(job.id)) : [];
      return {
        ...state,
        [action.key]: {
          ...entry,
          days: {
            ...entry.days,
            [action.date]: {
              total: loaded ? loaded.total : day.total,
              jobs: [...day.jobs, ...more],
              loading: false,
            },
          },
        },
      };
    }

    default:
      return state;
  }
}

/**
 * Store key for a board filter hash and time zone.
 *
 * @param {string} hash ProjectsBase.getHash() / getBriefsHash()
 * @param {string} timeZone
 * @returns {string}
 */
export const getExternalJobsKey = (hash, timeZone) => `${hash}|${timeZone}`;

/**
 * Board filters as GET /external-jobs params (same names as the Twine list).
 *
 * @param {object} filters
 * @returns {object}
 */
export const buildExternalJobsFilterParams = ({
  roles,
  experienceLevels,
  hiringCapacities,
  remote,
  internal,
  jobLocationText,
  searchTerm,
}) => {
  const params = {};
  if (roles && roles.length > 0) {
    params.roles = roles;
  }
  const levels = (experienceLevels || [])
    .filter((l) => l.isChecked)
    .map((l) => l.type);
  if (levels.length > 0) {
    params.experienceLevel = levels;
  }
  const capacities = (hiringCapacities || [])
    .filter((h) => h.isChecked)
    .map((h) => h.type);
  if (capacities.length > 0) {
    params.hiringCapacity = capacities;
  }
  if (remote) {
    params.remote = 1;
  }
  if (internal) {
    params.internal = 1;
  }
  if (jobLocationText) {
    params.inLocation = jobLocationText.replace(/-/g, ' ');
  }
  if (searchTerm) {
    params.searchTerm = searchTerm;
  }
  return params;
};

const externalJobsUrl = (params) =>
  `/external-jobs?${queryString.stringify(params, { arrayFormat: 'bracket' })}`;

/**
 * Load the first jobs and totals for each day from `from` to `to`.
 *
 * @param {string} key getExternalJobsKey()
 * @param {object} filterParams buildExternalJobsFilterParams()
 * @param {{from: string, to: string}} range
 * @param {string} timeZone
 * @returns {object}
 */
export function loadExternalJobDays(key, filterParams, { from, to }, timeZone) {
  return {
    types: [LOAD_DAYS, LOAD_DAYS_SUCCESS, LOAD_DAYS_FAIL],
    promise: (client) =>
      getWithRetry(
        client,
        externalJobsUrl({
          ...filterParams,
          from,
          to,
          tz: timeZone,
          per_day: EXTERNAL_JOBS_PER_DAY,
        })
      ),
    key,
    from,
    to,
    onServer: isServer(),
  };
}

/**
 * Let days whose load failed be requested again.
 *
 * @param {string} key
 * @returns {object}
 */
export function retryExternalJobDays(key) {
  return { type: RETRY_DAYS, key };
}

/**
 * Load the next jobs of one day ("Show all").
 *
 * @param {string} key
 * @param {object} filterParams
 * @param {string} date
 * @param {string} timeZone
 * @param {number} offset
 * @returns {object}
 */
export function loadMoreExternalJobs(
  key,
  filterParams,
  date,
  timeZone,
  offset
) {
  return {
    types: [LOAD_MORE, LOAD_MORE_SUCCESS, LOAD_MORE_FAIL],
    promise: (client) =>
      getWithRetry(
        client,
        externalJobsUrl({
          ...filterParams,
          from: date,
          to: date,
          tz: timeZone,
          per_day: EXTERNAL_JOBS_PER_PAGE,
          offset,
        })
      ),
    key,
    date,
  };
}
